// ExcelImportDialog.jsx
import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import * as XLSX from "xlsx";
import axios from "axios";
import { getErrorResponse, getHeader } from "../utils/util";
import { toast } from "react-toastify";

// ----- reusable component -----
export default function ExcelImportDialog({
  open,
  onClose,
  url,
  validateInput,
  getPayload,
}) {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleFile = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { header: 1 });
      if (json.length < 2) {
        setError(
          "The sheet must contain a header row and at least one data row."
        );
        return;
      }
      setColumns(json[0]);
      setRows(
        json
          .slice(1)
          .map((r, i) =>
            Object.fromEntries(json[0].map((h, j) => [h, r[j] ?? ""]))
          )
      );
    } catch (err) {
      setError("Unable to read file – please check the format.");
    }
  };

  const handleSubmit = async () => {
    const notOkMessage = validateInput(rows);
    if (notOkMessage) {
      setError(notOkMessage);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = getPayload(rows);
      const response = await axios.post(url, payload, getHeader());
      toast.success(response.data.message);
      onClose();
    } catch (error) {
      setError(getErrorResponse(error));
    } finally {
      setBusy(false);
    }
  };

  const resetAndClose = () => {
    setRows([]);
    setColumns([]);
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Import Data From Excel
        <IconButton
          aria-label="close"
          onClick={resetAndClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box display="flex" gap={2} alignItems="center" mb={2}>
          <Tooltip title="Accepts .xlsx or .xls">
            <Button
              component="label"
              startIcon={<UploadFileIcon />}
              variant="outlined"
            >
              Choose file
              <input
                hidden
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFile}
              />
            </Button>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            1. Download the template &nbsp;|&nbsp; 2. Fill it &nbsp;|&nbsp; 3.
            Import here
          </Typography>
        </Box>

        {busy && <LinearProgress />}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {rows.length > 0 && (
          <TableContainer
            sx={{ maxHeight: 300, border: 1, borderColor: "divider" }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell key={c}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow hover key={idx}>
                    {columns.map((c) => (
                      <TableCell key={c}>{row[c]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No data loaded yet. Please select an Excel file – the first row must
            contain column headers, and at least one additional data row is
            required.
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={resetAndClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={rows.length === 0 || busy}
        >
          Submit&nbsp;({rows.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
}
